import type { ClassDefinition } from '../../schemas/classDefinition';
import { chiericoAdvanced2to5Moves } from '../moves/chierico/advanced-2-5';
import { chiericoAdvanced6to10Moves } from '../moves/chierico/advanced-6-10';
import { chiericoStartingAutoMoves, chiericoStartingChoiceMoves } from '../moves/chierico/initial';

/**
 * Definizione della classe **Chierico** (manuale V1, rr. 2602-2961).
 *
 * Quinta classe trascritta. **Prima classe casteggiante con Tradizioni
 * Sacre**: stress-test del pattern di accesso "tutti gli spell della
 * Tradizione" (regola differente dal Mago "scegli K + cantrip auto").
 *
 * Pattern principali stress-testati:
 * - **`spellcasting.tradizioni` popolato per la prima volta** (5 valori:
 *   crepuscolo, guarigione, illuminazione, negromanzia, valore).
 * - **Mossa-config `Divinità` con `choices` strutturate** sulla Tradizione
 *   Sacra (5 opzioni, choiceCount 1). Il `MoveRef.selectedChoices[0]` di
 *   `chierico.divinita` è l'SSOT della Tradizione del PG. Sfera/Fondamento/
 *   Nome divinità rimangono in description (workaround D2 della PR).
 * - **Cross-class restriction `prerequisites.anyOf`** con forward-reference
 *   a mosse di altre classi non ancora trascritte (`druido.divinita-della-
 *   natura`, `paladino.ordine-sacro`, `monaco.monasteri`,
 *   `warlock.faro-di-devozione`). Pattern già supportato da FI-19 chiuso
 *   col Guerriero.
 * - **`replaces` su 4 mosse del Chierico** (Invincibilità Divina, Martire,
 *   Provvidenza, Magia Potenziata Superiore).
 *
 * **Punti notevoli**:
 * - Dado di danno **d6**, PF base 8, Carico base 8
 * - 5 archetipi razza-tematici
 * - 4 mosse iniziali automatiche + 3 mosse iniziali di scelta (`pickCount: 1`)
 * - 16 mosse avanzate L2-5, 14 mosse avanzate L6-10 (42 mosse totali)
 * - 4 template di Legami (= `maxBonds`, FI-3 rispettato)
 * - **Casteggiante** con 5 Tradizioni Sacre
 */
export const chiericoClassDefinition: ClassDefinition = {
  id: 'chierico',
  name: 'Chierico',

  intro:
    "Le terre di Dungeon World Rebrewed sono un crogiolo di disperazione, dove l'eco degli dèi sembra affievolirsi. Orrori antichi si risvegliano, i morti si ergono e le terre selvagge reclamano ciò che un tempo era civilizzato, minacciando le ultime roccaforti di speranza custodite dalla Chiesa. In questo mondo di vuoto spirituale la tua fede non è un sussurro, ma un grido che risuona anche nei luoghi più remoti! È la tua risorsa più preziosa, un dono che ti lega a qualcosa di più grande.\n\nNon sei solo un avventuriero, ma un servitore del divino, un tramite vivente della volontà superiore. Il tuo dio e il suo *Fondamento*, la *Sfera d'Influenza* che governa, e la *Tradizione Sacra* che ti ha svelato sono la tua guida in questo caos. Invochi la divinità, attingendo al suo potere spirituale, per proteggere i fedeli, guarire i sofferenti e sfidare l'oscurità che minaccia di inghiottire ogni cosa. Che tu sia un guerriero sacro che affronta gli orrori con la forza bruta del suo credo, un guaritore devoto che lenisce ogni sofferenza con la preghiera e la compassione, un saggio erudito che svela antichi segreti divini tra polverosi volumi, o persino un ecclesiastico astuto che naviga tra le intricate politiche della Chiesa, la tua devozione è un baluardo incrollabile contro i tuoi nemici.\n\nBrandisci la tua fede come uno scudo contro le tenebre più fitte, una spada affilata contro l'empio e divieni un faro per coloro che hanno perso la via. Sei qui per agire, non per giudicare. In queste lande dimenticate dagli dèi, sarai il loro messaggero, seminando speranza e dimostrando la potenza divina attraverso ogni tua azione: dalle semplici cure che rimarginano le ferite, a potenti benedizioni che cambiano il corso della battaglia. Quale sarà la tua eredità in questo mondo che brama disperatamente un segno di fede?",

  // Manuale V1, rr. 2613-2615
  suggestedNames: [
    // Umano
    'Lux',
    'Wesley',
    'Brinton',
    'Jon',
    'Sara',
    'Hawthorn',
    'Elise',
    'Clarke',
    'Lenore',
    'Piotr',
    'Dahlia',
    // Elfo
    'Dala',
    'Maetra',
    'Viusvel',
    'Rildris',
    'Cirthres',
    'Erilda',
    'Rowenrin',
    'Trawe',
    'Tahal',
    'Malea',
    // Nano
    'Zora',
    'Aelfar',
    'Gerda',
    'Rurgosh',
    'Bjorn',
    'Drummond',
    'Helga',
    'Siggrun',
    'Freya',
    // Halfling
    'Panman',
    'Inginius',
    'Willra',
    'Towise',
    'Dora',
    'Sedine',
    'Isenlia',
    'Thatella',
    'Wisedoc',
    // Mezz'orco
    'Sharkte',
    'Bolggar',
    'Beigor',
    'Uub',
    'Mashlùrtz',
    'Balcsha',
    'Tora',
    'Ba',
    'Gora',
    "Zu'hr",
  ],

  suggestedTitles: [],

  // Manuale V1, rr. 2617-2622
  aspetti: [
    {
      label: 'Occhi',
      options: ['Gentili', 'Acuti', 'Tristi'],
    },
    {
      label: 'Capelli',
      options: ['Tonsura', 'Capelli stravaganti', 'Calvo'],
    },
    {
      label: 'Vesti',
      options: ['Vesti fluenti', 'Tonaca', 'Abiti comuni'],
    },
    {
      label: 'Corpo',
      options: ['Esile', 'Tozzo', 'Flaccido'],
    },
  ],

  // Manuale V1, rr. 2625-2630
  damageDie: 'd6',
  baseHp: 8,
  baseLoad: 8,

  // Manuale V1, rr. 2632-2637
  alignmentPrecepts: {
    buono: 'Ti metti in pericolo per guarire qualcuno.',
    legale: 'Ti metti in pericolo per seguire i precetti della tua chiesa o della tua divinità.',
    neutrale: 'Recluti proseliti per la tua chiesa o per la tua divinità.',
    caotico: "Metti due fazioni l'una contro l'altra.",
    malvagio: 'Danneggi qualcuno per provare la superiorità della tua chiesa o della tua divinità.',
  },

  // Manuale V1, rr. 2660-2666. **maxBonds: 4** (4 templates ↔ 4, FI-3 rispettato).
  bondTemplates: [
    '_____ ha insultato la mia divinità, e questo avrà delle conseguenze.',
    '_____ è una persona di fede: mi fido ciecamente di lui.',
    '_____ è in costante pericolo: lo terrò al sicuro.',
    'Mi sto impegnando per convertire _____ alla mia fede.',
  ],
  maxBonds: 4,

  // Manuale V1, rr. 2670-2676
  archetypes: [
    {
      id: 'umano',
      name: 'Umano',
      associatedRace: 'umano',
      description:
        'Aggiungi alla tua Tradizione Sacra un incantesimo di primo livello dalla lista del Mago.',
      grantedMoveId: 'chierico.archetipo-umano',
    },
    {
      id: 'elfo',
      name: 'Elfo',
      associatedRace: 'elfo',
      description:
        'Quando *discerni la realtà*, aggiungi «Cosa prevede il mio destino riguardo la situazione corrente?» alle domande che puoi fare.',
      grantedMoveId: 'chierico.archetipo-elfo',
    },
    {
      id: 'nano',
      name: 'Nano',
      associatedRace: 'nano',
      description: "Aggiungi alla tua Tradizione Sacra l'orazione *Voce della Terra*.",
      grantedMoveId: 'chierico.archetipo-nano',
    },
    {
      id: 'halfling',
      name: 'Halfling',
      associatedRace: 'halfling',
      description:
        "Quando sei in un insediamento civilizzato pieno di devoti o in luogo sacro, ti si presenterà sempre l'opportunità di un incarico o di una missione per la tua chiesa o per il tuo dio.",
      grantedMoveId: 'chierico.archetipo-halfling',
    },
    {
      id: 'mezzorco',
      name: "Mezz'orco",
      associatedRace: 'mezzorco',
      description:
        'La tua divinità è terribile e vendicativa. Puoi sempre usare la minaccia della sua ira come leva per *parlamentare*.',
      grantedMoveId: 'chierico.archetipo-mezzorco',
    },
  ],

  startingMoves: chiericoStartingAutoMoves.map((m) => m.id),

  // Manuale V1, r. 2745: "Inoltre, scegli una delle seguenti mosse"
  startingChoiceGroups: [
    {
      id: 'mossa-iniziale-aggiuntiva',
      name: 'Mossa iniziale aggiuntiva',
      description: 'Scegli una di queste tre mosse oltre alle 4 iniziali automatiche.',
      moveOptions: chiericoStartingChoiceMoves.map((m) => m.id),
      pickCount: 1,
      expandableInAdvanced: false,
    },
  ],

  advancedMoves2to5: chiericoAdvanced2to5Moves.map((m) => m.id),
  advancedMoves6to10: chiericoAdvanced6to10Moves.map((m) => m.id),

  // **Prima applicazione di `tradizioni` non vuoto** in `SpellcastingConfig`.
  // Le 5 Tradizioni Sacre del Chierico (manuale r. 2729-2737):
  // Crepuscolo, Guarigione, Illuminazione, Negromanzia, Valore.
  // La Tradizione scelta dal PG vive in `MoveRef.selectedChoices[0]` della
  // mossa `chierico.divinita` (vedi D2 della PR).
  spellcasting: {
    cantripLabel: 'Orazioni',
    castingMoveId: 'chierico.lanciare-incantesimi-divini',
    manaMoveId: 'chierico.mana',
    tradizioni: ['crepuscolo', 'guarigione', 'illuminazione', 'negromanzia', 'valore'],
  },

  // Manuale V1, rr. 2640-2658
  startingEquipment: {
    always: [
      {
        id: 'razioni-da-dungeon',
        name: 'Razioni da Dungeon',
        weight: 1,
        uses: 5,
        tags: ['razione'],
      },
      {
        id: 'simbolo-divinita',
        name: 'Simbolo della divinità',
        weight: 0,
        tags: [],
        userDescriptionPrompt: 'Descrivilo a tua scelta.',
      },
    ],
    choices: [
      {
        id: 'difesa-iniziale',
        prompt: 'Scegli le tue difese',
        pickCount: 1,
        options: [
          {
            id: 'armatura-di-maglia',
            label: 'Armatura di maglia',
            items: [
              {
                id: 'armatura-di-maglia',
                name: 'Armatura di maglia',
                weight: 1,
                armor: 1,
                tags: ['indossato'],
              },
            ],
          },
          {
            id: 'pozione-curativa',
            label: 'Pozione Curativa',
            items: [
              {
                id: 'pozione-curativa-difesa',
                name: 'Pozione Curativa',
                weight: 0,
                uses: 1,
                tags: [],
              },
            ],
          },
        ],
      },
      {
        id: 'arma-iniziale',
        prompt: 'Scegli la tua arma',
        pickCount: 1,
        options: [
          {
            id: 'martello-piu-scudo',
            label: 'Martello da Guerra e Scudo',
            items: [
              {
                id: 'martello-da-guerra',
                name: 'Martello da Guerra',
                weight: 1,
                tags: ['media'],
              },
              {
                id: 'scudo',
                name: 'Scudo',
                weight: 2,
                armor: 1,
                tags: [],
                description: 'Quando usato.',
              },
            ],
          },
          {
            id: 'mazzafrusto',
            label: 'Mazzafrusto',
            items: [
              {
                id: 'mazzafrusto',
                name: 'Mazzafrusto',
                weight: 2,
                tags: ['media'],
                damage: '+1 danni',
              },
            ],
          },
          {
            id: 'bastone-piu-bende',
            label: 'Bastone e Bende',
            items: [
              {
                id: 'bastone',
                name: 'Bastone',
                weight: 1,
                tags: ['media', 'a due mani'],
              },
              {
                id: 'bende',
                name: 'Bende',
                weight: 0,
                uses: 3,
                tags: ['lento'],
              },
            ],
          },
        ],
      },
      {
        id: 'extra-iniziale',
        prompt: "Scegli un'opzione tra le seguenti",
        pickCount: 1,
        options: [
          {
            id: 'attrezzatura-da-avventuriero',
            label: 'Attrezzatura da Avventuriero',
            items: [
              {
                id: 'attrezzatura-da-avventuriero',
                name: 'Attrezzatura da Avventuriero',
                weight: 2,
                uses: 5,
                tags: [],
              },
            ],
          },
          {
            id: 'antitossina-x2',
            label: '2 Antitossina',
            items: [
              {
                id: 'antitossina',
                name: 'Antitossina',
                weight: 0,
                uses: 1,
                quantity: 2,
                tags: [],
              },
            ],
          },
        ],
      },
    ],
  },
};
